// ---------------------------------------------------------------------------
// PWA_ControlBillet — Bottom navigation bar
// 4 tabs: Scanner, Historique, Stats, Config. Touch-optimized.
// ---------------------------------------------------------------------------

import { useAppStore, type Screen } from '@/store/app-store';

// ---------------------------------------------------------------------------
// Inline SVG icons (no external library)
// ---------------------------------------------------------------------------

function ScannerIcon({ active }: { active: boolean }) {
  const color = active ? 'text-accent-light' : 'text-gray-400';
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={color}
    >
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <line x1="7" y1="12" x2="17" y2="12" />
    </svg>
  );
}

function HistoryIcon({ active }: { active: boolean }) {
  const color = active ? 'text-accent-light' : 'text-gray-400';
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={color}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function StatsIcon({ active }: { active: boolean }) {
  const color = active ? 'text-accent-light' : 'text-gray-400';
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={color}
    >
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

function ConfigIcon({ active }: { active: boolean }) {
  const color = active ? 'text-accent-light' : 'text-gray-400';
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={color}
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Tab definition
// ---------------------------------------------------------------------------

interface NavTab {
  screen: Screen;
  label: string;
  Icon: React.ComponentType<{ active: boolean }>;
}

const TABS: NavTab[] = [
  { screen: 'scanner', label: 'Scanner', Icon: ScannerIcon },
  { screen: 'history', label: 'Historique', Icon: HistoryIcon },
  { screen: 'stats', label: 'Stats', Icon: StatsIcon },
  { screen: 'config', label: 'Config', Icon: ConfigIcon },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BottomNav() {
  const screen = useAppStore((s) => s.screen);
  const navigate = useAppStore((s) => s.navigate);

  return (
    <nav className="fixed bottom-0 left-0 right-0 h-[72px] z-40 bg-surface-light border-t border-white/10 flex items-center justify-around px-2 safe-area-pb">
      {TABS.map(({ screen: tabScreen, label, Icon }) => {
        const isActive = screen === tabScreen;
        return (
          <button
            key={tabScreen}
            type="button"
            onClick={() => navigate(tabScreen)}
            className={`flex flex-col items-center justify-center gap-1 flex-1 min-h-[44px] py-2 rounded-lg transition-colors duration-150 ${
              isActive ? 'bg-accent/10' : 'active:bg-white/5'
            }`}
          >
            <Icon active={isActive} />
            <span
              className={`text-[10px] font-medium ${
                isActive ? 'text-accent-light' : 'text-gray-400'
              }`}
            >
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
